import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import { PAGINATION_INITIAL_STATE } from "./constants"

// ============ LIST ============
const requested = createAction("discounts/requested")
const received = createAction("discounts/received")
const failed = createAction("discounts/failed")

// ============ CREATE ============
const requestedCreate = createAction("discounts/requestedCreate")
const receivedCreate = createAction("discounts/receivedCreate")
const failedCreate = createAction("discounts/failedCreate")

// ============ UPDATE ============
const requestedUpdate = createAction("discounts/requestedUpdate")
const receivedUpdate = createAction("discounts/receivedUpdate")
const failedUpdate = createAction("discounts/failedUpdate")

// ============ TOGGLE ============
const requestedToggle = createAction("discounts/requestedToggle")
const receivedToggle = createAction("discounts/receivedToggle")
const failedToggle = createAction("discounts/failedToggle")

// ============ CHECK CODE ============
const requestedCheckCode = createAction("discounts/requestedCheckCode")
const receivedCheckCode = createAction("discounts/receivedCheckCode")
const failedCheckCode = createAction("discounts/failedCheckCode")

// ============ DELETE ============
const requestedDelete = createAction("discounts/requestedDelete")
const receivedDelete = createAction("discounts/receivedDelete")
const failedDelete = createAction("discounts/failedDelete")

// ============ INITIAL STATE ============
const initialState = {
  ...LOADING_INITIAL_STATE,
  items: [],
  metadata: { ...PAGINATION_INITIAL_STATE },
  createDiscount: { ...LOADING_INITIAL_STATE },
  updateDiscount: { ...LOADING_INITIAL_STATE },
  toggleDiscount: { ...LOADING_INITIAL_STATE },
  deleteDiscount: { ...LOADING_INITIAL_STATE },
  checkCode: { ...LOADING_INITIAL_STATE }
}

// ============ REDUCER ============
const discountsReducer = createReducer(initialState, {
  [requested.type]: (state) => { setRequested(state) },
  [received.type]: (state, { payload }) => {
    setReceived(state)
    state.items = payload.data || payload
    if (payload.metadata) Object.assign(state.metadata, payload.metadata)
  },
  [failed.type]: (state) => { setFailed(state) },

  [requestedCreate.type]: (state) => { setRequested(state.createDiscount) },
  [receivedCreate.type]: (state) => { setReceived(state.createDiscount) },
  [failedCreate.type]: (state) => { setFailed(state.createDiscount) },

  [requestedUpdate.type]: (state) => { setRequested(state.updateDiscount) },
  [receivedUpdate.type]: (state) => { setReceived(state.updateDiscount) },
  [failedUpdate.type]: (state) => { setFailed(state.updateDiscount) },

  [requestedToggle.type]: (state) => { setRequested(state.toggleDiscount) },
  [receivedToggle.type]: (state) => { setReceived(state.toggleDiscount) },
  [failedToggle.type]: (state) => { setFailed(state.toggleDiscount) },

  [requestedCheckCode.type]: (state) => { setRequested(state.checkCode) },
  [receivedCheckCode.type]: (state) => { setReceived(state.checkCode) },
  [failedCheckCode.type]: (state) => { setFailed(state.checkCode) },

  [requestedDelete.type]: (state) => { setRequested(state.deleteDiscount) },
  [receivedDelete.type]: (state) => { setReceived(state.deleteDiscount) },
  [failedDelete.type]: (state) => { setFailed(state.deleteDiscount) }
})

export default discountsReducer

// ============ API ACTIONS ============

export const getDiscounts = (params = {}) =>
  api({
    url: "/guide/settings/discounts",
    method: "get",
    params,
    onStart: requested.type,
    onSuccess: received.type,
    onError: failed.type
  })

export const createDiscount = (data) =>
  api({
    url: "/guide/settings/discounts",
    method: "post",
    data,
    onStart: requestedCreate.type,
    onSuccess: receivedCreate.type,
    onError: failedCreate.type,
    showSuccessNotification: true
  })

export const updateDiscount = (id, data) =>
  api({
    url: `/guide/settings/discounts/${id}`,
    method: "put",
    data,
    onStart: requestedUpdate.type,
    onSuccess: receivedUpdate.type,
    onError: failedUpdate.type,
    showSuccessNotification: true
  })

export const toggleDiscount = (id) =>
  api({
    url: `/guide/settings/discounts/${id}/toggle`,
    method: "put",
    onStart: requestedToggle.type,
    onSuccess: receivedToggle.type,
    onError: failedToggle.type,
    showSuccessNotification: true
  })

export const deleteDiscount = (id) =>
  api({
    url: `/guide/settings/discounts/${id}`,
    method: "delete",
    onStart: requestedDelete.type,
    onSuccess: receivedDelete.type,
    onError: failedDelete.type,
    props: { id },
    showSuccessNotification: true
  })

export const checkDiscountCode = (code) =>
  api({
    url: "/guide/settings/discounts/check_code",
    method: "post",
    data: { code },
    onStart: requestedCheckCode.type,
    onSuccess: receivedCheckCode.type,
    onError: failedCheckCode.type,
    suppressErrorNotification: true
  })

export const validateDiscount = (code, amount, experienceId, clientIdentifier, addOnsTotal) =>
  api({
    url: "/guide/settings/discounts/validate",
    method: "post",
    data: { code, amount, experience_id: experienceId, client_identifier: clientIdentifier, add_ons_total: addOnsTotal },
    suppressErrorNotification: true
  })

// The booking widgets' check of a promo code, open to guests who aren't signed in (the guide's
// validate needs a guide). Same answer as validateDiscount: 200 with the code, 422 with
// message.base, and 429 when a guest checks too many codes. A preview only: payment checks the code
// again. Takes validateDiscount's arguments; add-ons are not sent (the API ignores them).
export const validateWidgetDiscount = (code, amount, experienceUuid, clientIdentifier) =>
  api({
    url: `/embedded_experiences/${experienceUuid}/discounts/validate`,
    method: "post",
    data: { code, amount, client_identifier: clientIdentifier },
    noAuth: true,
    suppressErrorNotification: true
  })
